import React, { createContext, useState, useEffect } from "react";

import { Tag } from "types/community";
import { TagService } from "StrapiClient/community/TagService";
import { useAuth } from "./AuthHook";

const CommunitySharedContext = createContext(undefined);

const useCommunityShared = () => {
  const context = React.useContext(CommunitySharedContext);
  if (context === undefined) {
    throw new Error(
      "`useCommunityShared` hook must be used within a `CommunitySharedContextProvider` component"
    );
  }
  return context;
};

const CommunitySharedContextProvider = ({ children }) => {
  const [blogTags, setBlogTags] = useState<Tag[]>([]);
  const [podcastTags, setPodcastTags] = useState<Tag[]>([]);
  const [videoTags, setVideoTags] = useState<Tag[]>([]);
  const [courseTags, setCourseTags] = useState<Tag[]>([]);

  const { authenticatedUser } = useAuth();

  useEffect(() => {
    if (authenticatedUser) {
      const fetchTags = async () => {
        try {
          const response = await TagService.getAll();
          if (response.data) {
            const tags = response.data || [];

            // Filter tags by type
            setBlogTags(
              tags
                .filter((tag: Tag) => tag.type === "blog")
                .map((tag: Tag) => tag.title)
            );
            setPodcastTags(
              tags
                .filter((tag: Tag) => tag.type === "podcast")
                .map((tag: Tag) => tag.title)
            );
            setVideoTags(
              tags
                .filter((tag: Tag) => tag.type === "video")
                .map((tag: Tag) => tag.title)
            );
            setCourseTags(
              tags
                .filter((tag: Tag) => tag.type === "course")
                .map((tag: Tag) => tag.title)
            );
          }
        } catch (error) {
          console.error("Error fetching tags:", error);
        }
      };

      fetchTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser?.username]);

  return (
    <CommunitySharedContext.Provider
      value={{
        blogTags,
        podcastTags,
        videoTags,
        courseTags,
      }}
    >
      {children}
    </CommunitySharedContext.Provider>
  );
};

export { CommunitySharedContextProvider, useCommunityShared };
